import { useEffect, useState } from "react";
import { api, fmtEur, fmtDate, formatErr, canWrite, COMPANY_COLORS, CATEGORIE_LABELS, currentCompetenzaMese } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ExportPdfButton } from "@/components/ExportPdfButton";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MonthPicker } from "@/components/MonthPicker";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Check, AlertTriangle, Trash2, Split } from "lucide-react";
import { toast } from "sonner";
import SplitDialog, { QuoteEditor, quoteRows, quoteOk } from "@/components/SplitDialog";
import { PadroncinoSelect, AZIENDA } from "@/components/PadroncinoSelect";
import { BancheDialog } from "@/components/BancheDialog";
import { RegoleDialog } from "@/components/RegoleDialog";
import { useSearchParams } from "react-router-dom";
import { EditableText } from "@/components/EditableText";
import { MezzoSelect } from "@/components/MezzoSelect";
import { RiconciliazionePanel } from "@/components/RiconciliazionePanel";
import { Link2 } from "lucide-react";
import { Search, X } from "lucide-react";

const ALL = "__all";
const NO_F = { q: "", data: "", tipo: ALL, categoria: ALL, descrizione: "", banca: ALL, padroncino: "", company: ALL, importo: "", stato: ALL };

const TIPO_OPTS = [{ value: "entrata", label: "Entrata" }, { value: "uscita", label: "Uscita" }];
const tipoLabel = (t) => (t === "entrata" ? "entrata" : "uscita");
const tipoDb = (form) => (form.tipo === "entrata" ? "entrata" : ["bonifico_diretto", "bonifico_terzi"].includes(form.categoria) ? "pagamento" : "spesa");
const lordo = (m) => m.totale_banca ?? m.importo;
const nomeM = (v) => new Date(`${v}-01T12:00`).toLocaleDateString("it-IT", { month: "long" });
const ContoMese = ({ m, writable, onPick }) => {
  const opts = [mesePrec(m.data), m.data.slice(0, 7)];
  if (!opts.includes(m.mese_competenza)) opts.push(m.mese_competenza);
  return writable ? (
    <select value={m.mese_competenza} onChange={(e) => onPick(e.target.value)} title="In quale conteggio va"
            className="block mt-0.5 text-[10px] bg-transparent border rounded-sm px-1 py-0 text-muted-foreground" data-testid={`pn-conteggio-${m.id}`}>
      {opts.map((o) => <option key={o} value={o}>cont. {nomeM(o)}</option>)}
    </select>
  ) : <div className="text-[10px] text-muted-foreground">cont. {nomeM(m.mese_competenza)}</div>;
};
const mesePrec = (d) => { const [y, m] = d.slice(0, 7).split("-").map(Number); return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`; };
const CAT_OPTS = Object.entries(CATEGORIE_LABELS).map(([v, l]) => ({ value: v, label: l }));

export default function PrimaNota() {
  const { company } = useCompany();
  const { user } = useAuth();
  const writable = canWrite(user, "movimenti");
  const [counts, setCounts] = useState({ all: 0, review: 0 });
  const [params] = useSearchParams();
  const [mese, setMese] = useState(params.get("mese") || currentCompetenzaMese());
  const [tab, setTab] = useState(params.get("tab") || "all");
  const [items, setItems] = useState([]);
  const [padroncini, setPadroncini] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [splitMov, setSplitMov] = useState(null);
  const [multi, setMulti] = useState(false);
  const [quote, setQuote] = useState({});
  const [banche, setBanche] = useState([]);
  const [f, setF] = useState(NO_F);
  const [veicoli, setVeicoli] = useState([]);
  const loadBanche = () => api.get("/banche", { params: { company } }).then(r => setBanche(r.data));

  const load = async () => {
    const params = { company, mese };
    if (tab === "review") params.needs_review = true;
    const { data } = await api.get("/movimenti", { params });
    setItems(data);
    const all = tab === "all" ? data : (await api.get("/movimenti", { params: { company, mese } })).data;
    const ric = (await api.get("/riconciliazione", { params: { company } })).data.length;
    setCounts({ all: all.length, review: all.filter((m) => m.needs_review).length, ric });
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [company, mese, tab]);
  useEffect(() => { api.get("/padroncini", { params: { company } }).then(r => setPadroncini(r.data)); api.get("/veicoli", { params: { company } }).then(r => setVeicoli(r.data)); loadBanche(); /* eslint-disable-line */ }, [company]);

  const pMap = Object.fromEntries(padroncini.map(p => [p.id, `${p.nome || ""} ${p.cognome || ""}`.trim() || p.codice]));

  const openNew = () => {
    const today = new Date().toISOString().slice(0, 10);
    setForm({ company: company === "all" ? "golmar" : company, mese_competenza: mese,
              data: today.startsWith(mese) ? today : `${mese}-01`,
              tipo: "uscita", categoria: "altre_spese", importo: 0, descrizione: "", fonte: "manuale" });
    setMulti(false); setQuote({});
    setOpen(true);
  };
  const save = async () => {
    let createdId = null;
    try {
      const body = { ...form, tipo: tipoDb(form), mese_competenza: mesePrec(form.data) };
      const { data } = await api.post("/movimenti", multi ? { ...body, padroncino_id: null } : body);
      createdId = data.id;
      if (multi) await api.post(`/movimenti/${data.id}/suddividi`, { quote: quoteRows(quote) });
      toast.success(multi ? `Spesa suddivisa su ${quoteRows(quote).length} padroncini` : "Creato");
      setOpen(false); load();
    }
    catch (e) {
      if (multi && createdId) await api.delete(`/movimenti/${createdId}`).catch(() => {});
      toast.error(formatErr(e.response?.data?.detail));
    }
  };
  const cambiaPadroncino = (m, pid) => patch(m, pid === AZIENDA ? { uso_aziendale: true } : { padroncino_id: pid }, pid === AZIENDA ? "Assegnato all'azienda: va nelle spese aziendali del Risultato" : "Padroncino aggiornato");
  const patch = async (m, body, msg = "Aggiornato") => {
    try { await api.patch(`/movimenti/${m.id}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const confirm = async (m) => {
    try { await api.patch(`/movimenti/${m.id}`, { needs_review: false, riconciliato: true }); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Elimina movimento?")) return;
    try { await api.delete(`/movimenti/${id}`); load(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const has = (s, t) => (s || "").toLowerCase().includes(t.trim().toLowerCase());
  const shown = items.filter((m) => {
    const pn = pMap[m.padroncino_id] || "";
    const pc = padroncini.find(p => p.id === m.padroncino_id)?.codice || "";
    if (f.q && ![m.descrizione, m.note, pn, pc, m.banca, CATEGORIE_LABELS[m.categoria], fmtDate(m.data), String(m.importo)].some(x => has(x, f.q))) return false;
    if (f.data && !has(fmtDate(m.data), f.data)) return false;
    if (f.descrizione && !has(`${m.descrizione} ${m.note || ""}`, f.descrizione)) return false;
    if (f.padroncino && !has(`${pc} ${pn}`, f.padroncino)) return false;
    if (f.importo && !has(fmtEur(m.importo), f.importo) && !has(String(m.importo), f.importo)) return false;
    if (f.tipo !== ALL && tipoLabel(m.tipo) !== f.tipo) return false;
    if (f.categoria !== ALL && m.categoria !== f.categoria) return false;
    if (f.company !== ALL && m.company !== f.company) return false;
    if (f.banca !== ALL && (f.banca === "__none" ? !!m.banca : m.banca !== f.banca)) return false;
    if (f.stato !== ALL && (f.stato === "review") !== !!m.needs_review) return false;
    return true;
  });
  const filtri = JSON.stringify(f) !== JSON.stringify(NO_F);
  const bancheOpts = [...new Set([...banche.map(b => b.abbreviazione), ...items.map(m => m.banca).filter(Boolean)])];
  const tot = shown.reduce((a, m) => a + (m.tipo === "entrata" ? lordo(m) : -lordo(m)), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-heading font-black">Prima nota</h1>
        <div className="flex items-center gap-2">
          <Label className="text-xs font-heading uppercase">Mese</Label>
          <MonthPicker value={mese} onChange={setMese} testId="pn-mese-input" />
          <ExportPdfButton resource="movimenti" params={{ company, mese, ...(tab === "review" ? { needs_review: true } : {}) }} />
          <BancheDialog banche={banche} company={company} onChange={loadBanche} />
          <RegoleDialog company={company} mese={mese} writable={writable} />
          {writable && <Button size="sm" onClick={openNew} className="h-8 gap-1" data-testid="pn-new-button">
            <Plus className="w-4 h-4" />Nuovo
          </Button>}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="pn-tab-all">Tutti ({counts.all})</TabsTrigger>
          <TabsTrigger value="review" data-testid="pn-tab-review" className="gap-1">
            <AlertTriangle className="w-3 h-3" />Coda eccezioni ({counts.review})
          </TabsTrigger>
          <TabsTrigger value="ric" data-testid="pn-tab-ric" className="gap-1">
            <Link2 className="w-3 h-3" />Riconciliazione ({counts.ric ?? 0})
          </TabsTrigger>
        </TabsList>
        <div className="flex items-center gap-2 mt-3">
          <div className="relative w-80">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Cerca in tutte le colonne..." className="h-8 pl-7" data-testid="pn-search" />
          </div>
          {filtri && <Button size="sm" variant="ghost" className="h-8 gap-1" onClick={() => setF(NO_F)} data-testid="pn-clear-filters"><X className="w-3.5 h-3.5" />Azzera filtri</Button>}
          <span className="text-xs text-muted-foreground" data-testid="pn-shown-count">{shown.length} di {items.length} movimenti</span>
          <span className="text-xs font-semibold text-orange-600" data-testid="pn-competenza-hint">Questi movimenti vanno nel conteggio di {new Date(`${mesePrec(mese)}-01T12:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</span>
        </div>
        {tab !== "ric" && <TabsContent value={tab} className="mt-3">
          <Card className="rounded-md overflow-hidden">
            <table className="autotrans-table w-full">
              <thead>
                <tr>
                  <th>Data</th><th>Tipo</th><th>Categoria</th><th>Descrizione</th><th>Banca</th>
                  <th>Padroncino</th><th>Mezzo</th><th>Azienda</th><th className="text-right">Importo</th><th>Stato</th><th className="w-32">Azioni</th>
                </tr>
                <tr className="pn-filter-row">
                  <th><FText v={f.data} on={(v) => setF({ ...f, data: v })} id="data" ph="gg/mm" /></th>
                  <th><FSel v={f.tipo} on={(v) => setF({ ...f, tipo: v })} id="tipo" opts={TIPO_OPTS} /></th>
                  <th><FSel v={f.categoria} on={(v) => setF({ ...f, categoria: v })} id="categoria" opts={CAT_OPTS} /></th>
                  <th><FText v={f.descrizione} on={(v) => setF({ ...f, descrizione: v })} id="descrizione" /></th>
                  <th><FSel v={f.banca} on={(v) => setF({ ...f, banca: v })} id="banca"
                            opts={[{ value: "__none", label: "Non indicata" }, ...bancheOpts.map(b => ({ value: b, label: b }))]} /></th>
                  <th><FText v={f.padroncino} on={(v) => setF({ ...f, padroncino: v })} id="padroncino" /></th>
                  <th></th>
                  <th><FSel v={f.company} on={(v) => setF({ ...f, company: v })} id="company" opts={["golmar", "vitam", "rotta66", "fmg"].map(c => ({ value: c, label: c }))} /></th>
                  <th><FText v={f.importo} on={(v) => setF({ ...f, importo: v })} id="importo" /></th>
                  <th><FSel v={f.stato} on={(v) => setF({ ...f, stato: v })} id="stato" opts={[{ value: "review", label: "Da rivedere" }, { value: "ok", label: "OK" }]} /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && <tr><td colSpan={11} className="text-center py-6 text-muted-foreground">{items.length ? "Nessun movimento con questi filtri" : "Nessun movimento"}</td></tr>}
                {shown.map((m) => (
                  <tr key={m.id} className={m.needs_review ? "bg-orange-50 dark:bg-orange-900/10" : ""} data-testid={`pn-row-${m.id}`}>
                    <td className="whitespace-nowrap">{fmtDate(m.data)}
                      <ContoMese m={m} writable={writable} onPick={(v) => patch(m, { mese_competenza: v }, `Spostata nel conteggio di ${nomeM(v)}`)} /></td>
                    <td><Badge variant="outline" className="h-5" data-testid={`pn-tipo-${m.id}`}>{tipoLabel(m.tipo)}</Badge></td>
                    <td className="min-w-[150px]">{writable ? (
                      <Select value={m.categoria} onValueChange={(v) => patch(m, { categoria: v }, "Categoria aggiornata")}>
                        <SelectTrigger className="h-7 text-xs" data-testid={`pn-categoria-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{CAT_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>) : (CATEGORIE_LABELS[m.categoria] || m.categoria)}</td>
                    <td className="max-w-md"><EditableText value={m.descrizione} disabled={!writable}
                          onSave={(v) => patch(m, { descrizione: v }, "Descrizione aggiornata")} testId={`pn-descr-${m.id}`} /></td>
                    <td className="min-w-[110px]">{writable ? (
                      <Select value={m.banca || "__none"} onValueChange={(v) => patch(m, { banca: v === "__none" ? "" : v }, "Banca aggiornata")}>
                        <SelectTrigger className={`h-7 text-xs ${m.fonte === "banca" && !m.banca ? "border-orange-500 text-orange-600" : ""}`} data-testid={`pn-banca-${m.id}`}>
                          <SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">{m.fonte === "banca" ? "Non riconosciuta" : "—"}</SelectItem>
                          {bancheOpts.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>) : (m.banca || "—")}</td>
                    <td className="min-w-[180px]">{writable ? (
                      <PadroncinoSelect value={m.uso_aziendale ? AZIENDA : m.padroncino_id || ""} onChange={(v) => cambiaPadroncino(m, v)} aziendaOption
                        padroncini={padroncini.filter((p) => p.company === m.company)} testId={`pn-padroncino-${m.id}`} />
                      ) : (m.uso_aziendale ? "AZIENDA (uso interno)" : pMap[m.padroncino_id] || "—")}</td>
                    <td className="min-w-[120px]"><MezzoSelect m={m} veicoli={veicoli} disabled={!writable}
                          onChange={(v) => patch(m, { veicolo_id: v }, "Mezzo aggiornato")} testId={`pn-mezzo-${m.id}`} /></td>
                    <td><Badge style={{ backgroundColor: COMPANY_COLORS[m.company], color: "white" }} className="h-5">{m.company}</Badge></td>
                    <td className={`text-right font-mono ${m.tipo === "entrata" ? "text-green-600" : "text-destructive"}`}>
                      {m.tipo === "entrata" ? "+" : "-"}{fmtEur(lordo(m))}
                      {m.totale_banca != null && <div className="text-[10px] text-muted-foreground" data-testid={`pn-iva-${m.id}`}>imp. {fmtEur(m.importo)} + IVA {fmtEur(m.iva)}</div>}
                    </td>
                    <td>{m.da_riconciliare ? <Badge className="bg-orange-500 h-5" data-testid={`pn-da-ric-${m.id}`}>Da riconciliare</Badge> : m.needs_review ? <Badge className="bg-orange-500 h-5">Da rivedere</Badge> : <Badge className="bg-green-600 h-5">OK</Badge>}</td>
                    <td className="whitespace-nowrap">
                      {writable && m.needs_review && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600"
                                onClick={() => confirm(m)} data-testid={`pn-confirm-${m.id}`}>
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      {writable && <Button size="icon" variant="ghost" className="h-7 w-7" title="Suddividi tra più padroncini"
                              onClick={() => setSplitMov(m)} data-testid={`pn-split-${m.id}`}>
                        <Split className="w-3.5 h-3.5" />
                      </Button>}
                      {writable && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive"
                              onClick={() => del(m.id)} data-testid={`pn-delete-${m.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              {shown.length > 0 && (
                <tfoot>
                  <tr><td colSpan={8} className="text-right font-heading font-bold">Saldo netto{filtri ? " (filtrato)" : ""}</td>
                      <td className={`text-right font-mono font-bold ${tot >= 0 ? "text-green-600" : "text-destructive"}`}>{fmtEur(tot)}</td>
                      <td colSpan={2}></td></tr>
                </tfoot>
              )}
            </table>
          </Card>
        </TabsContent>}
        {tab === "ric" && <TabsContent value="ric" className="mt-3">
          <RiconciliazionePanel company={company} pMap={pMap} writable={writable} onChange={load} />
        </TabsContent>}
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={multi ? "max-w-2xl" : ""}>
          <DialogHeader><DialogTitle>Nuovo movimento</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Azienda"><Select value={form.company} onValueChange={(v) => setForm({ ...form, company: v })}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["golmar", "vitam", "rotta66", "fmg"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select></Field>
            <Field label="Data"><Input type="date" value={form.data || ""} onChange={(e) => setForm({ ...form, data: e.target.value })} className="h-8" data-testid="pn-field-data" /></Field>
            <Field label="Tipo"><Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{TIPO_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select></Field>
            <Field label="Categoria"><Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{CAT_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select></Field>
            <Field label="Importo (€)"><Input type="number" step="0.01" value={form.importo ?? 0}
                                               onChange={(e) => setForm({ ...form, importo: parseFloat(e.target.value || 0) })} className="h-8" data-testid="pn-field-importo" /></Field>
            <Field label="Padroncino">{multi ? <div className="h-8 text-sm text-muted-foreground flex items-center">Più padroncini (vedi sotto)</div> :
              <PadroncinoSelect value={form.padroncino_id || ""} onChange={(v) => setForm({ ...form, padroncino_id: v || null })} allowEmpty
                padroncini={padroncini.filter((p) => p.company === form.company)} placeholder="—" className="h-8 w-full" testId="pn-field-padroncino" />}</Field>
            <Field label="Banca"><Select value={form.banca || "__none"} onValueChange={(v) => setForm({ ...form, banca: v === "__none" ? null : v })}>
              <SelectTrigger className="h-8" data-testid="pn-field-banca"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="__none">—</SelectItem>{bancheOpts.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select></Field>
            <div className="col-span-2"><Field label="Descrizione"><Input value={form.descrizione || ""}
                   onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="h-8" data-testid="pn-field-descrizione" /></Field></div>
            <div className="col-span-2"><Field label="Seconda riga sul conteggio (es. SCAD. 23/01/2027 - RATA 1/2)"><Input value={form.note || ""}
                   onChange={(e) => setForm({ ...form, note: e.target.value })} className="h-8" data-testid="pn-field-note" /></Field></div>
            <label className="col-span-2 flex items-center gap-2 text-sm font-medium">
              <Checkbox checked={multi} onCheckedChange={(v) => setMulti(!!v)} data-testid="pn-multi-toggle" />
              Fattura di più padroncini: suddividi l'importo
            </label>
            {multi && <div className="col-span-2"><QuoteEditor padroncini={padroncini.filter((p) => p.company === form.company && p.is_active)}
              totale={form.importo || 0} quote={quote} setQuote={setQuote} /></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={multi && !quoteOk(quote, form.importo || 0)} data-testid="pn-save-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SplitDialog movimento={splitMov} onClose={() => setSplitMov(null)} onDone={() => { setSplitMov(null); load(); }} />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-xs font-heading uppercase tracking-wider">{label}</Label><div className="mt-1">{children}</div></div>
);

const FText = ({ v, on, id, ph = "filtra" }) => (
  <Input value={v} onChange={(e) => on(e.target.value)} placeholder={ph} className="h-6 text-xs px-1.5 bg-background text-foreground font-normal normal-case" data-testid={`pn-filter-${id}`} />
);

const FSel = ({ v, on, id, opts }) => (
  <Select value={v} onValueChange={on}>
    <SelectTrigger className="h-6 text-xs px-1.5 bg-background text-foreground font-normal normal-case" data-testid={`pn-filter-${id}`}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value={ALL}>Tutti</SelectItem>{opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
  </Select>
);
